import { ChevronLeft, ChevronRight, Star } from "lucide-react";

export default function CustomerTable({
  customers = [],
  onView,
  onEdit,
  onDelete,
  onToggleFav,
}) {
  return (
    <div className="w-full overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-slate-200 p-6 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Customer Records</h2>
          <p className="mt-1 text-sm text-slate-500">Manage all customer profiles</p>
        </div>
        <div className="inline-flex items-center justify-center rounded-2xl bg-slate-100 px-5 py-3 font-semibold text-slate-800">
          Total : {customers.length}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr className="text-left text-[15px] font-semibold text-slate-600">
              <th className="px-7 py-5">★</th>
              <th className="px-7 py-5">SN</th>
              <th className="px-7 py-5">Customer</th>
              <th className="px-7 py-5">Phone</th>
              <th className="px-7 py-5">Branch</th>
              <th className="px-7 py-5 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {customers.length === 0 ? (
              <tr>
                <td colSpan={6} className="h-[330px] border-t border-slate-100">
                  <div className="flex h-full flex-col items-center justify-center">
                    <h3 className="text-3xl font-bold text-slate-900">No Customers Found</h3>
                    <p className="mt-3 text-slate-500">Try changing filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              customers.map((customer, index) => (
                <tr key={customer.id} className="border-t border-slate-100 hover:bg-slate-50 transition">
                  <td className="px-7 py-5">
                    <button
                      onClick={() => onToggleFav && onToggleFav(customer.id, customer.favourite)}
                      data-testid={`customer-fav-${customer.id}`}
                    >
                      <Star
                        size={18}
                        className={customer.favourite ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-400"}
                      />
                    </button>
                  </td>

                  <td className="px-7 py-5 font-semibold">{index + 1}</td>

                  <td className="px-7 py-5">
                    <div>
                      <h4 className="font-semibold text-slate-900">{customer.customer_name}</h4>
                      <p className="text-sm text-slate-500">{customer.customer_id}</p>
                    </div>
                  </td>

                  <td className="px-7 py-5">{customer.phone}</td>
                  <td className="px-7 py-5">{customer.branch}</td>

                  <td className="px-7 py-5">
                    <div className="flex justify-center gap-2">
                      <button onClick={() => onView(customer.id)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                        View
                      </button>
                      <button onClick={() => onEdit(customer.id)} className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600">
                        Edit
                      </button>
                      <button onClick={() => onDelete(customer.id)} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="space-y-4 p-4 lg:hidden">
        {customers.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
            <h3 className="text-xl font-bold text-slate-900">No Customers Found</h3>
            <p className="mt-2 text-sm text-slate-500">Try changing filters</p>
          </div>
        ) : (
          customers.map((customer, index) => (
            <div key={customer.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="flex items-center gap-1.5 font-bold text-slate-900">
                  <button onClick={() => onToggleFav && onToggleFav(customer.id, customer.favourite)}>
                    <Star size={16} className={customer.favourite ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                  </button>
                  {customer.customer_name}
                </h3>
                <span className="rounded-lg bg-slate-100 px-3 py-1 text-xs font-semibold">#{index + 1}</span>
              </div>

              <div className="mt-4 space-y-2 text-sm">
                <p><span className="font-semibold">Customer ID:</span> {customer.customer_id}</p>
                <p><span className="font-semibold">Phone:</span> {customer.phone}</p>
                <p><span className="font-semibold">Branch:</span> {customer.branch}</p>
              </div>

              <div className="mt-5 flex gap-2">
                <button onClick={() => onView(customer.id)} className="flex-1 rounded-xl bg-blue-600 py-2 text-white">View</button>
                <button onClick={() => onEdit(customer.id)} className="flex-1 rounded-xl bg-amber-500 py-2 text-white">Edit</button>
                <button onClick={() => onDelete(customer.id)} className="flex-1 rounded-xl bg-red-600 py-2 text-white">Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex flex-col gap-5 border-t border-slate-200 p-5 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-sm font-medium text-slate-500">
          Showing {customers.length} Customer{customers.length !== 1 ? "s" : ""}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <select className="h-12 rounded-2xl border border-slate-200 bg-white px-5 text-sm font-medium outline-none">
            <option>1 - 100</option>
            <option>1 - 50</option>
            <option>1 - 25</option>
            <option>1 - 10</option>
          </select>
          <button className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-400">
            <ChevronLeft size={18} />
          </button>
          <button className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black font-semibold text-white">1</button>
          <button className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
